import {
  Box,
  Button,
  Divider,
  TextField,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import ArrowBack from '@material-ui/icons/ArrowBack';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { debounce } from 'lodash';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { z } from 'zod';
import { getRules } from '../../../../actions/rules.actions';
import { setLoading } from '../../../../actions/system.actions';
import {
  deleteUnitGuestPermit,
  deleteUnitTenantPermit,
  getAddendumPdf,
  getUnit,
  patchUnitStatus,
  updateUnitContact,
  updateUnitGuestVehicles,
  updateUnitGuestPermit,
  updateUnitTenantVehicles,
  updateUnitTenantPermit,
} from '../../../../actions/units.actions';
import VppDatePicker from '../../../../components/datePicker/vppDatePicker';
import { formatValidationErrors, getApiErrorMessage } from '../../../../utils';
import {
  formatPermitFieldErrors,
  reshapeBackendPermitErrors,
  resendActivationLinkSchema,
  unitPermitSchema,
} from '../../../../validators';
import { normalizeLicenceState } from './licenceStateUtils';
import ArchiveUnit from '../archive/ArchiveUnit';
import UnitsContactInformationBlock from './ContactInfoBlock';
import ResendActivationLink from './ResendActivationLink';
import VehiclesSection from './VehiclesSection';

const DEBOUNCE_TIME = 300;

const UNIT_STATUS_SELECT_OPTIONS = ['active', 'blocked', 'suspended'].map(
  (value) => ({ name: value, value })
);

const EDITABLE_UNIT_STATUSES = ['active', 'blocked', 'suspended'];

const padVehicleSlots = (vehicles, count) => {
  const list = Array.isArray(vehicles) ? [...vehicles] : [];
  const target = Math.max(list.length, parseInt(count, 10) || 0);

  if (target <= list.length) {
    return list;
  }

  // Unique objects per slot — shared {} from fill({}) mutates across cards.
  return [
    ...list,
    ...Array.from({ length: target - list.length }, () => ({})),
  ];
};

const useStyles = makeStyles((theme) => ({
  backButtonText: {
    textTransform: 'capitalize',
    color: '#BDBDBD',
  },
  backButtonIcon: {
    fontSize: '34px!important', // TODO: Refactor needed
    left: '-8px',

    position: 'relative',
  },
  unitNumberField: {
    border: `1px solid ${theme.palette.primary.main}`,
    width: 140,
    fontSize: 20,
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  unitNumberFieldInput: {
    textAlign: 'center',
    paddingTop: '2.5px',
    paddingBottom: '2.5px',
  },
}));

moment.tz.setDefault('America/Chicago');

const getExpirationDate = (guest_hours_start, guest_hours_finish, vehicle) => {
  if (!guest_hours_start || !guest_hours_finish) {
    console.log({ guest_hours_start, guest_hours_finish });
    return;
  }
  // get the hours and minutes from vehicle.expiration_date
  if (guest_hours_start === guest_hours_finish) {
    let time = moment(vehicle.issue_date).format('H:mm').split(':');
    return moment(vehicle.expiration_date)
      .set({ hours: time[0], minutes: time[1] })
      .toDate();
  } else {
    let time = guest_hours_finish.split(':');
    return moment(vehicle.expiration_date)
      .set({ hours: time[0], minutes: time[1] })
      .toDate();
  }
};

const CONTACT_SECTION_FIELDS = [
  'unit_number',
  'first_name',
  'last_name',
  'email',
  'phone_number',
  'tenant_vehicles_number',
  'daily_guest_vehicles_number',
  'status',
];

const EditUnits = ({ match }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const rootRef = useRef(null);
  const system = useSelector((state) => state.system);
  const rules = useSelector((state) => state.rules);
  const { enqueueSnackbar } = useSnackbar();

  const [isArchived, setIsArchived] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [isContactEditMode, setIsContactEditMode] = useState(false);
  const [tenantVehicles, setTenantVehicles] = useState([]);
  const [guestVehicles, setGuestVehicles] = useState([]);
  const [newTenantPermitIndex, setNewTenantPermitIndex] = useState(null);
  const [newGuestPermitIndex, setNewGuestPermitIndex] = useState(null);
  const [vehiclesRevision, setVehiclesRevision] = useState(0);
  const [reservedSpots, setReservedSpots] = useState([]);
  const [status, setStatus] = useState('');
  const [isUnitLoaded, setIsUnitLoaded] = useState(false);
  const [disabledAddendum, setDisabledAddendum] = useState(false);
  const [newActivationData, setNewActivationData] = useState({});
  const savedUnitStatusRef = useRef('');

  const history = useHistory();

  const serializeDate = (value) => {
    if (!value) {
      return value;
    }
    if (moment.isMoment(value)) {
      return value.toISOString();
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    return value;
  };

  const {
    errors,
    register,
    setValue,
    getValues,
    handleSubmit,
    control,
    watch,
    clearErrors,
    trigger,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: {
      status: '',
    },
  });
  const { unitId } = match.params;
  const { guest_hours_finish, guest_hours_start } = rules;

  const getData = useCallback(() => {
    window.scrollTo(0, 0);
    setIsUnitLoaded(false);
    setStatus('');
    dispatch(setLoading(true));
    return dispatch(getUnit(unitId))
      .then(({ data }) => {
        setDisabledAddendum(data.status !== 'active');
        setIsArchived(data.status === 'archived');
        // Saved permits come back first from the API. Pad empty slots after them
        // and write those padded arrays into the form so a save at index 2 cannot
        // leave stale plate/state values on the empty cards that take its place.
        const paddedTenantVehicles = padVehicleSlots(
          data.tenant_vehicles,
          data.tenant_vehicles_number
        );
        const paddedGuestVehicles = padVehicleSlots(
          data.guest_vehicles,
          data.daily_guest_vehicles_number
        );
        setTenantVehicles(paddedTenantVehicles);
        setGuestVehicles(paddedGuestVehicles);
        Object.keys(data).forEach((item) => {
          setValue(item, data[item]);
          if (item === 'status') {
            setStatus(data[item]);
            savedUnitStatusRef.current = data[item];
            return;
          }
          if (item === 'reserved_spots') {
            setReservedSpots(data[item]);
          }
          if (
            item === 'first_name' ||
            item === 'last_name' ||
            item === 'phone_number' ||
            item === 'email' ||
            item === 'unit_number'
          ) {
            setNewActivationData((prev) => ({
              ...prev,
              [item]: data[item],
            }));
          }
        });
        setValue('tenant_vehicles', paddedTenantVehicles);
        setValue('guest_vehicles', paddedGuestVehicles);
        setVehiclesRevision((revision) => revision + 1);
        setIsUnitLoaded(true);
        setNewTenantPermitIndex(null);
        setNewGuestPermitIndex(null);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  }, [dispatch, setValue, unitId]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    dispatch(getRules());
  }, [dispatch]);

  useEffect(() => {
    register('status');
  }, [register]);

  const handleDownloadAddemdumPdf = () => {
    dispatch(getAddendumPdf(unitId)).then(({ data }) => {
      window.open(data.parking_addendum, '_blank');
    });
  };

  const handleAddTenant = () => {
    setTenantVehicles((vehicles) => {
      const nextIndex = vehicles.length;
      setNewTenantPermitIndex(nextIndex);
      return [...vehicles, {}];
    });
  };

  const handleAddGuest = () => {
    setGuestVehicles((vehicles) => {
      const nextIndex = vehicles.length;
      setNewGuestPermitIndex(nextIndex);
      return [...vehicles, {}];
    });
  };

  const prepareTenantVehicle = (vehicle) => {
    if (!vehicle?.issue_date) {
      return null;
    }
    const prepared = { ...vehicle };
    if (
      !vehicle.licence_plate ||
      !vehicle.color ||
      !vehicle.make ||
      !vehicle.model ||
      !vehicle.licence_state
    ) {
      prepared.expiration_date = '';
    } else {
      prepared.issue_date = serializeDate(vehicle.issue_date);
      prepared.expiration_date = serializeDate(vehicle.expiration_date);
    }
    return prepared;
  };

  const prepareGuestVehicle = (vehicle) => {
    if (!vehicle?.issue_date) {
      return null;
    }
    const prepared = { ...vehicle };
    const expirationDate = getExpirationDate(
      guest_hours_start,
      guest_hours_finish,
      vehicle
    );
    if (
      !vehicle.licence_plate ||
      !vehicle.color ||
      !vehicle.make ||
      !vehicle.model ||
      !vehicle.licence_state
    ) {
      delete prepared.expiration_date;
    } else {
      prepared.expiration_date = serializeDate(expirationDate);
      prepared.issue_date = serializeDate(vehicle.issue_date);
    }
    return prepared;
  };

  const prepareVehicleSection = (type, vehicles) => {
    const prepareVehicle =
      type === 'tenant' ? prepareTenantVehicle : prepareGuestVehicle;

    return (vehicles || [])
      .filter((vehicle) => vehicle?.issue_date)
      .map(prepareVehicle)
      .filter(Boolean);
  };

  const updateVehicleSection = (type, vehicles) => {
    const vehiclesKey =
      type === 'tenant' ? 'tenant_vehicles' : 'guest_vehicles';
    const updateAction =
      type === 'tenant'
        ? updateUnitTenantVehicles
        : updateUnitGuestVehicles;

    return dispatch(
      updateAction(unitId, {
        [vehiclesKey]: prepareVehicleSection(type, vehicles),
      })
    );
  };

  const prepareContactPayload = useCallback(
    (data, countOverrides = {}) => {
      const payload = {
        unit_number: data.unit_number,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone_number: data.phone_number,
        tenant_vehicles_number:
          countOverrides.tenant ?? parseInt(data.tenant_vehicles_number, 10),
        daily_guest_vehicles_number:
          countOverrides.guest ??
          parseInt(data.daily_guest_vehicles_number, 10),
      };

      if (rules?.allow_tenant_to_own_reserved_spots) {
        payload.reserved_spots =
          data.reserved_spots !== undefined
            ? data.reserved_spots
            : reservedSpots;
      }

      return payload;
    },
    [reservedSpots, rules?.allow_tenant_to_own_reserved_spots]
  );

  const savePermit = (type, vehicleIndex) => {
    const vehiclesKey =
      type === 'tenant' ? 'tenant_vehicles' : 'guest_vehicles';
    const vehicle = getValues()[vehiclesKey]?.[vehicleIndex] || {};

    try {
      unitPermitSchema.parse({
        ...vehicle,
        licence_plate: String(vehicle.licence_plate || '')
          .trim()
          .toUpperCase(),
        licence_state: normalizeLicenceState(vehicle.licence_state),
      });
    } catch (err) {
      if (err instanceof z.ZodError) {
        setFieldErrors(formatPermitFieldErrors(vehiclesKey, vehicleIndex, err));
        enqueueSnackbar(
          'Please correct the errors before saving.',
          { variant: 'error' }
        );
        return Promise.reject();
      }

      throw err;
    }

    const payload =
      type === 'tenant'
        ? prepareTenantVehicle(vehicle)
        : prepareGuestVehicle(vehicle);

    if (!payload) {
      enqueueSnackbar('Permit data is incomplete.', { variant: 'error' });
      return Promise.reject();
    }

    setFieldErrors({});
    setSubmitDisabled(true);
    dispatch(setLoading(true));

    const saveAction =
      type === 'tenant'
        ? updateUnitTenantPermit(unitId, payload)
        : updateUnitGuestPermit(unitId, payload);

    return dispatch(saveAction)
      .then(async () => {
        if (type === 'tenant') {
          setNewTenantPermitIndex(null);
        } else {
          setNewGuestPermitIndex(null);
        }
        // Keep the loader up through unit refresh so the new card is ready.
        await getData();
        enqueueSnackbar('Permit updated successfully', { variant: 'success' });
      })
      .catch((error) => {
        const errorData = error?.response?.data || {};
        setFieldErrors(
          reshapeBackendPermitErrors(errorData, vehiclesKey, vehicleIndex)
        );
        const apiMessage = getApiErrorMessage(errorData);
        enqueueSnackbar(
          apiMessage ||
            'Error updating permit. Please make sure all fields are filled in correctly.',
          { variant: 'error' }
        );
        dispatch(setLoading(false));
        return Promise.reject(error);
      })
      .finally(() => {
        setSubmitDisabled(false);
      });
  };

  const cancelPermit = (type, vehicleIndex) => {
    if (type === 'tenant') {
      setNewTenantPermitIndex(null);
    } else {
      setNewGuestPermitIndex(null);
    }
    setFieldErrors({});
    getData();
  };

  const getPermittedVehicleCount = (type) => {
    const field =
      type === 'tenant'
        ? 'tenant_vehicles_number'
        : 'daily_guest_vehicles_number';
    return Math.max(0, parseInt(getValues(field), 10) || 0);
  };

  const applyVehicleSlots = (type, vehicles) => {
    const padded = padVehicleSlots(vehicles, getPermittedVehicleCount(type));
    if (type === 'tenant') {
      setTenantVehicles(padded);
      setNewTenantPermitIndex(null);
    } else {
      setGuestVehicles(padded);
      setNewGuestPermitIndex(null);
    }
    setValue(`${type}_vehicles`, padded);
  };

  const handleDeleteVehicle = async (id, type, savedVehicleId = null) => {
    const stateVehicles = type === 'tenant' ? tenantVehicles : guestVehicles;
    const formVehicles = getValues(`${type}_vehicles`) || [];
    const selectedVehicle = {
      ...(stateVehicles[id] || {}),
      ...(formVehicles[id] || {}),
    };
    const permitId = savedVehicleId || selectedVehicle.id;

    const remainingVehicles = stateVehicles.filter((_, index) => index !== id);

    // Unsaved / empty cards: remove locally, pad back to Contact counts, and
    // remount so the card leaves edit mode (view + EDIT), matching Cancel.
    if (!permitId) {
      applyVehicleSlots(type, remainingVehicles);
      setFieldErrors({});
      clearErrors(`${type}_vehicles`);
      setVehiclesRevision((revision) => revision + 1);
      return;
    }

    setSubmitDisabled(true);
    dispatch(setLoading(true));

    try {
      if (type === 'guest') {
        await dispatch(deleteUnitGuestPermit(unitId, permitId));
      } else {
        await dispatch(deleteUnitTenantPermit(unitId, permitId));
      }

      // Keep the loader up through unit refresh so the card does not linger.
      await getData();
      enqueueSnackbar('Permit deleted successfully', { variant: 'success' });
    } catch (error) {
      const errorData = error?.response?.data || {};
      enqueueSnackbar(
        getApiErrorMessage(errorData) || 'Error deleting permit.',
        { variant: 'error' }
      );
      dispatch(setLoading(false));
    } finally {
      setSubmitDisabled(false);
    }
  };

  const handleChangeVehicleNumber = useCallback(
    debounce((count, type) => {
      const typedHook =
        type === 'tenant' ? setTenantVehicles : setGuestVehicles;
      const vehicles = type === 'tenant' ? tenantVehicles : guestVehicles;

      const vehiclesLength = vehicles.filter(
        (vehicle) => vehicle !== undefined
      ).length;

      if (count > vehiclesLength) {
        typedHook((vehicles) => {
          return [
            ...vehicles,
            ...Array.from({ length: count - vehiclesLength }, () => ({})),
          ];
        });
      } else if (vehiclesLength > count) {
        typedHook((vehicles) => {
          const newArray = [...vehicles];
          newArray.splice(-(vehiclesLength - count));
          return newArray;
        });
      }
    }, DEBOUNCE_TIME),
    [setGuestVehicles, setTenantVehicles, tenantVehicles, guestVehicles]
  );

  const handleChangeTenantNumber = (e) => {
    const count = parseInt(e.target.value, 10);
    handleChangeVehicleNumber(count, 'tenant');
  };

  const handleChangeGuestNumber = (e) => {
    const count = parseInt(e.target.value, 10);
    handleChangeVehicleNumber(count, 'guest');
  };

  const handleCancelSection = (section) => {
    if (section === 'contact') {
      setIsContactEditMode(false);
    }
    setFieldErrors({});
    getData();
  };

  const onSubmit = useCallback(
    (data, section) => {
      setFieldErrors({});

      let savePromise;
      let statusChangeAttempted = false;

      try {
        if (section === 'contact') {
          resendActivationLinkSchema.parse(data);

          // The base flow submitted both complete vehicle arrays with contact
          // changes. Keep that reconciliation behavior through the split APIs so
          // reducing a configured count still removes permits outside the list.
          savePromise = Promise.all([
            updateVehicleSection('tenant', tenantVehicles),
            updateVehicleSection('guest', guestVehicles),
          ]).then(() =>
            dispatch(
              updateUnitContact(unitId, prepareContactPayload(data))
            )
          );

          const normalizedStatus = String(data.status || '')
            .toLowerCase()
            .trim();
          const savedStatus = String(savedUnitStatusRef.current || '')
            .toLowerCase()
            .trim();
          if (
            EDITABLE_UNIT_STATUSES.includes(normalizedStatus) &&
            normalizedStatus !== savedStatus
          ) {
            statusChangeAttempted = true;
            savePromise = savePromise.then(() =>
              dispatch(patchUnitStatus(normalizedStatus, unitId))
                .then(() => ({ statusUpdated: true }))
                .catch((statusError) => {
                  console.error('Unit status update failed:', statusError);
                  return { statusUpdated: false, statusError };
                })
            );
          }
        }

        if (!savePromise) {
          return;
        }

        setSubmitDisabled(true);
        dispatch(setLoading(true));

        savePromise
          .then((result) => {
            if (section === 'contact' && statusChangeAttempted) {
              if (result?.statusUpdated === false) {
                const statusMessage = getApiErrorMessage(
                  result.statusError?.response?.data
                );
                enqueueSnackbar(
                  statusMessage ||
                    'Contact information saved, but unit status could not be updated.',
                  { variant: 'warning' }
                );
                setIsContactEditMode(false);
                getData();
                rootRef.current?.scrollIntoView?.({ behavior: 'smooth' });
                return;
              }

              if (result?.statusUpdated && data.status) {
                const normalizedStatus = String(data.status)
                  .toLowerCase()
                  .trim();
                setStatus(normalizedStatus);
                savedUnitStatusRef.current = normalizedStatus;
                setDisabledAddendum(normalizedStatus !== 'active');
              }
            }

            enqueueSnackbar('Unit updated successfully', {
              variant: 'success',
            });
            setIsContactEditMode(false);
            getData();
            rootRef.current?.scrollIntoView?.({ behavior: 'smooth' });
          })
          .catch((error) => {
            const errorData = error?.response?.data || {};
            setFieldErrors(errorData);
            const apiMessage = getApiErrorMessage(errorData);
            enqueueSnackbar(
              apiMessage ||
                'Error updating unit. Please make sure all fields are filled in correctly.',
              {
                variant: 'error',
              }
            );
            rootRef.current?.scrollIntoView?.({ behavior: 'smooth' });
          })
          .finally(() => {
            setSubmitDisabled(false);
            dispatch(setLoading(false));
          });
      } catch (err) {
        if (err instanceof z.ZodError) {
          window.scrollTo(0, 0);
          setFieldErrors(formatValidationErrors(err));
        }
      }
    },
    [
      dispatch,
      enqueueSnackbar,
      getData,
      guestVehicles,
      prepareContactPayload,
      tenantVehicles,
      unitId,
    ]
  );

  const handleSaveContact = async () => {
    // Validate only contact-section fields so an open/unsaved permit card
    // cannot silently block contact save via shared-form validation.
    const isValid = await trigger(CONTACT_SECTION_FIELDS);
    if (!isValid) {
      return;
    }
    onSubmit(getValues(), 'contact');
  };

  return (
    <Box mr={4} pb={4}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        ref={rootRef}
      >
        <Button
          onClick={() => {
            history.goBack();
          }}
          startIcon={
            <ArrowBack color="primary" className={classes.backButtonIcon} />
          }
        >
          <Typography color="textSecondary" className={classes.backButtonText}>
            Back
          </Typography>
        </Button>
      </Box>
      <Box mt={2} mx={-4}>
        <Divider />
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box my={3}>
          <Box display="flex" alignItems="center" mb={3} pl={3}>
            <Box display="flex" alignItems="center">
              <Box mr={2}>
                <Typography color="primary" variant="h6">
                  Unit number
                </Typography>
              </Box>
              <TextField
                name="unit_number"
                size="small"
                inputRef={register}
                error={!!fieldErrors.unit_number}
                helperText={
                  fieldErrors.unit_number ? fieldErrors.unit_number : null
                }
                InputProps={{
                  classes: {
                    root: classes.unitNumberField,
                    input: classes.unitNumberFieldInput,
                  },
                  readOnly: !isContactEditMode,
                }}
              />
            </Box>
            <Box display="flex" alignItems="center" ml={4}>
              <Box mr={2}>
                <Typography>Issue date</Typography>
              </Box>
              <VppDatePicker control={control} name="issue_date" disabled />
            </Box>
            <Box ml={4}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<CloudUploadIcon />}
                onClick={() => handleDownloadAddemdumPdf()}
                disabled={disabledAddendum}
              >
                PARKING ADDENDUM
              </Button>
            </Box>
            {isUnitLoaded && status === 'pending' && (
              <ResendActivationLink
                unitId={unitId}
                newActivationData={newActivationData}
                onSuccess={getData}
              />
            )}
          </Box>

          <UnitsContactInformationBlock
            inputRefs={register}
            fieldErrors={fieldErrors}
            disabled={!isContactEditMode}
            isEditing={isContactEditMode}
            onEdit={() => setIsContactEditMode(true)}
            onCancel={() => handleCancelSection('contact')}
            onSave={() => {
              clearErrors();
              handleSaveContact();
            }}
            showActions={!isArchived}
            submitDisabled={submitDisabled}
            onChangeTenantNumber={handleChangeTenantNumber}
            onChangeGuestNumber={handleChangeGuestNumber}
            reservedSpots={reservedSpots}
            getValues={getValues}
            setValue={setValue}
            control={control}
            isUnitLoaded={isUnitLoaded}
            unitStatus={
              isUnitLoaded
                ? String(watch('status') || status || '')
                    .toLowerCase()
                    .trim()
                : ''
            }
            statusSelectOptions={UNIT_STATUS_SELECT_OPTIONS}
          />

          <VehiclesSection
            type="tenant"
            title="Permitted tenant vehicles"
            control={control}
            register={register}
            vehicles={tenantVehicles}
            fieldErrors={fieldErrors}
            vehicleTitle="Vehicle"
            buttonId="add_tenant_permit_button"
            addButtonText="ADD PERMIT"
            addButtonHandler={handleAddTenant}
            deleteButtonHandler={handleDeleteVehicle}
            showActions={!isArchived}
            submitDisabled={submitDisabled}
            newPermitIndex={newTenantPermitIndex}
            onSavePermit={(index) => savePermit('tenant', index)}
            onCancelPermit={(index) => cancelPermit('tenant', index)}
            watch={watch}
            getValues={getValues}
            setValue={setValue}
            formErrors={errors}
            isUnitLoaded={isUnitLoaded}
            vehiclesRevision={vehiclesRevision}
          />

          <VehiclesSection
            type="guest"
            title="Permitted daily guest vehicles"
            control={control}
            register={register}
            vehicles={guestVehicles}
            fieldErrors={fieldErrors}
            buttonId="add_guest_permit_button"
            vehicleTitle="Guest Vehicle"
            addButtonText="ADD PERMIT"
            addButtonHandler={handleAddGuest}
            deleteButtonHandler={handleDeleteVehicle}
            showActions={!isArchived}
            submitDisabled={submitDisabled}
            newPermitIndex={newGuestPermitIndex}
            onSavePermit={(index) => savePermit('guest', index)}
            onCancelPermit={(index) => cancelPermit('guest', index)}
            watch={watch}
            getValues={getValues}
            setValue={setValue}
            formErrors={errors}
            isUnitLoaded={isUnitLoaded}
            vehiclesRevision={vehiclesRevision}
          />

          <Box display="flex" alignItems="center" mt={4} pl={3}>
            {!isArchived ? (
              <ArchiveUnit
                unitId={unitId}
                renderOpen={(handleClickOpen) => (
                  <Button
                    id="unit_archive_button"
                    variant="outlined"
                    size="large"
                    color="primary"
                    onClick={handleClickOpen}
                  >
                    Archive
                  </Button>
                )}
              />
            ) : !system.loading ? (
              <Typography variant="h6" color="primary">
                Archived
              </Typography>
            ) : null}
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default EditUnits;
